import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

type ImageItem = { src: string; alt: string };

export default function ReservationSlider(props: {
  images: ImageItem[];
  path: string;
  /** fraction of the container width that should peek from the next slide (0..0.5). Default 0.2 = 20% */
  peek?: number;
}) {
  const { images = [], path, peek = 0.2 } = props;
  const { t } = useTranslation();

  const [active, setActive] = useState(0);
  const trackRef = useRef<HTMLDivElement | null>(null);
  // drag removed: slider is not draggable via pointer/mouse

  const length = Array.isArray(images) ? images.length : 0;

  // clamp peek to reasonable bounds
  const peekClamped = Math.min(
    0.5,
    Math.max(0, Number.isFinite(peek) ? peek : 0.2)
  );
  const slideWidthPercent = 1 - peekClamped; // e.g. 0.8 for 20% peek
  const sidePaddingPercent = peekClamped / 2; // we'll pad container on both sides equally

  useEffect(() => {
    if (active >= length) setActive(Math.max(0, length - 1));
  }, [length, active]);

  // update active index on scroll (snap)
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    let raf = 0;
    function onScroll() {
      // throttle using rAF
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        const container = trackRef.current;
        if (!container) {
          raf = 0;
          return;
        }
        const children = Array.from(container.children) as HTMLElement[];
        // find the child whose left is closest to container's scrollLeft
        const scrollLeft = container.scrollLeft;
        const widths = children.map((c) => c.offsetLeft);
        let closestIdx = 0;
        let closestDist = Infinity;
        widths.forEach((left, i) => {
          const dist = Math.abs(left - scrollLeft);
          if (dist < closestDist) {
            closestDist = dist;
            closestIdx = i;
          }
        });
        setActive(closestIdx);
        raf = 0;
      });
    }

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [trackRef, images]);

  // keyboard support
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "ArrowLeft") scrollToIndex(active - 1);
      if (e.key === "ArrowRight") scrollToIndex(active + 1);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active]);

  // dragging removed

  function scrollToIndex(i: number) {
    if (!trackRef.current) return;
    const idx = Math.max(0, Math.min(length - 1, i));
    const child = trackRef.current.children[idx] as HTMLElement | undefined;
    if (child) {
      // smooth scroll so the child aligns with the start of the viewport minus left padding
      const container = trackRef.current;
      const left =
        child.offsetLeft -
        Math.round(container.clientWidth * sidePaddingPercent);
      container.scrollTo({ left, behavior: "smooth" });
    }
  }

  if (!length) return null;

  return (
    <div className="py-5">
      <div className="w-full overflow-y-hidden h-64">
        <div
          ref={trackRef}
            className={`overflow-x-scroll overflow-y-hidden scroll-smooth snap-x snap-mandatory flex pb-4`}
            style={{
              paddingLeft: `${sidePaddingPercent * 100}%`,
              paddingRight: `${sidePaddingPercent * 100}%`,
            }}
            role="list"
          aria-label={t("Gallery")}
        >
          {images.map((image, index) => (
            <div
              key={index}
              className="snap-start flex-shrink-0 relative"
              style={{
                width: `${slideWidthPercent * 100}%`,
                paddingLeft: `1.25rem`,
              }}
              role="listitem"
              aria-current={index === active}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") scrollToIndex(index);
              }}
              onClick={() => scrollToIndex(index)}
            >
              <div className="absolute top-2 right-2 max-w-[75%] bg-black/50 text-white text-fluid-xs rounded-md px-2 py-1 z-10">
                {t(image.alt)}
              </div>

              <img
                className={`rounded-lg object-cover w-full h-64 ${(index === active + 1 || index === active - 1 ) ? 'cursor-pointer' : ''}`}
                src={path + t(image.src)}
                loading="lazy"
                alt={t(image.alt)}
                draggable={false}
              />
            </div>
          ))}
        </div>
      </div>

      {/* clickable indicators */}
      {length > 1 && (
        <div className="flex justify-center mt-5 px-5">
          {images.map((_, i) => (
            <button
              key={i}
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => scrollToIndex(i)}
              className={`w-[calc(100%/${images.length})] h-1 ${
                i === active ? "bg-black" : "bg-custom-300"
              }`}
            />
          ))}
        </div>
      )}
      {/* screen reader announcement for active slide */}
      <div aria-live="polite" className="sr-only">
        {images[active] ? t(images[active].alt) : ""}
      </div>
    </div>
  );
}
